import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import videoSrc from "@/assets/heroBackground.mp4";

const Hero: React.FC = () => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const subHighlightRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLAnchorElement>(null);

  const maxFadeScroll = 1000;
  const [overlayOpacity, setOverlayOpacity] = useState(1);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // entrance animations
    [
      { ref: headingRef, delay: 0 },
      { ref: highlightRef, delay: 300 },
      { ref: subHighlightRef, delay: 600 },
      { ref: buttonRef, delay: 900 },
    ].forEach(({ ref, delay }) => {
      if (ref.current) {
        setTimeout(() => {
          ref.current!.classList.add("tw-translate-y-0", "tw-opacity-100");
        }, delay);
      }
    });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const prog = Math.min(y / maxFadeScroll, 1);
      setOverlayOpacity(1 - prog);
      setProgress(prog);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // BASE dimensions (fractions)
  const BASE_BAR_H = 0.20; // 20% tall
  const BASE_BAR_W = 0.50; // 50% wide
  const SPACE = 0.05; // 5% gap
  const CENTER_X = 0.70; // 70% from left

  // fixed vertical positions
  const midY = 0.40;                     // middle bar top‐edge
  const topY = midY - BASE_BAR_H - SPACE; // top bar top‐edge
  const botY = midY + BASE_BAR_H + SPACE; // bottom bar top‐edge

  // how much to scale overlay by (1→2)
  const scale = 1 + progress; // tweak “+1” if you want a different max zoom

  // where to pivot the zoom: middle bar’s center
  const originX = CENTER_X * 100 + "%";
  const originY = (midY + BASE_BAR_H / 2) * 100 + "%";

  const fadedOpacity = overlayOpacity;

  return (
    <div className="tw-relative" style={{ height: `calc(100vh + ${maxFadeScroll}px)` }}>
      {/* 1) Define the mask with three fixed holes */}
   <svg width="0" height="0" style={{ position: "absolute" }}>
        <mask id="mask-horz-e" maskUnits="objectBoundingBox" maskContentUnits="objectBoundingBox">
          <rect x="0" y="0" width="1" height="1" fill="white" />

          <rect
            x={CENTER_X - BASE_BAR_W / 2}
            y={topY}
            width={BASE_BAR_W}
            height={BASE_BAR_H}
            fill="black"
            fillOpacity={fadedOpacity}
          />

          <rect
            x={CENTER_X - BASE_BAR_W / 2}
            y={midY}
            width={BASE_BAR_W}
            height={BASE_BAR_H}
            fill="black"
          />

          <rect
            x={CENTER_X - BASE_BAR_W / 2}
            y={botY}
            width={BASE_BAR_W}
            height={BASE_BAR_H}
            fill="black"
            fillOpacity={fadedOpacity}
          />
        </mask>
      </svg> 

      <section
        className="tw-relative tw-flex tw-items-center tw-justify-start tw-pt-24 tw-pb-24 tw-overflow-hidden"
        style={{ position: "sticky", top: 0, height: "100vh" }}
      >
        {/* 2) Video background */}
         <video
          className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
          src={videoSrc}
          autoPlay
          loop
          muted
          onContextMenu={(e) => e.preventDefault()}
          playsInline
        />


        {/* 3) The zooming, masked overlay */}
         <div
          className="tw-absolute tw-inset-0 tw-pointer-events-none"
          style={{
            opacity: overlayOpacity,
            backgroundColor: "black",
            mask: `url(#mask-horz-e) no-repeat`,
            WebkitMask: `url(#mask-horz-e) no-repeat`,
            maskSize: "100% 100%",
            WebkitMaskSize: "100% 100%",
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",

            // key zoom transform
            transform: `scale(${scale})`,
            transformOrigin: `${originX} ${originY}`,
            transition: "transform 0.1s ease-out",
          }}
        /> 

        {/* 4) Text + Button */}
        <div className="tw-relative tw-z-30 tw-max-w-7xl tw-px-6 md:tw-px-12 lg:tw-pl-20 tw-text-white">
          <h1
            ref={headingRef}
            className="tw-font-extrabold tw-text-[28px] sm:tw-text-[40px] md:tw-text-[52px] lg:tw-text-[56px] tw- leading-tight tw-space-y-2 tw-mb-8 tw--translate-y-8 tw-opacity-0 tw-transition-all tw-duration-700 tw-ease-out"
          >
            <div>India’s most powerful post‑</div>
            <div ref={subHighlightRef}> purchase platform,</div>
            <div
              ref={highlightRef}
              className="tw-text-[#00C5FB] tw--translate-y-8 tw-opacity-0 tw-transition-all tw-duration-700 tw-delay-300"
            >
              driven by AI
            </div>
          </h1>

          <Link
            to="/schedule-demo"
            ref={buttonRef}
            className="tw-inline-block tw-bg-[#00C5FB] tw-text-black tw-font-semibold tw-text-sm sm:tw-text-base tw-py-2.5 tw-px-6 tw-rounded-full tw--translate-y-8 tw-opacity-0 tw-transition-all tw-duration-700 tw-ease-out hover:tw-bg-white hover:tw-text-cyan-600"
          >
            Schedule a Demo
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Hero;